import { Injectable } from '@angular/core';
import { IdType } from '../enums/IdType.enum';
import { Roles } from '../enums/Roles.enum';
import { FeatureFlagService } from './feature-flag.service';

/**
 * FIN-1708 · the two decisions the pre-approval deposit popup rests on.
 *
 * They live here rather than inline in `app.component.ts` for the reason FIN-1709 moved
 * the money capabilities into a service: that file is ~1900 lines and has no spec, so a
 * condition written inside it is a condition nobody can test. Both of these decide
 * something a production investor feels — one of them whether they are asked to sign at
 * all — and neither should be verifiable only by clicking.
 *
 * `app.component.ts` keeps the wiring (which dialog, which router call); this keeps the
 * answers.
 */
@Injectable({
  providedIn: 'root',
})
export class PreApprovalPopupService {
  constructor(private featureFlagService: FeatureFlagService) {}

  /**
   * D68 · whether submitting the LAFT form hands off to the popup instead of opening the
   * Keynua widget.
   *
   * ⚠ This is the gate that decides whether a real investor is asked to sign. `laft-pn-v2`
   * is a LaunchDarkly flag bucketed on `user._id`, so at a 50 % rollout both answers are
   * live in production at once — flag off must keep today's behaviour exactly
   * (requirement 15.1), which is why the flag is tested here and not assumed from
   * context.
   */
  shouldDeferSignature(profile: any, submitResponse: any): boolean {
    if (!this.featureFlagService.isEarlyDepositEnabled()) {
      return false;
    }

    // ⚠ FIN-1710 (§2.13, D89) · the verdict, and it comes off the **submit response**,
    // never off `this.profile`: that document is pre-submit and its `isPreApproved` is
    // stale — the trap FIN-1708 documented at `app.component.ts:871-882`.
    //
    // Until FIN-1710 this deferred for every investor PN under the flag, which the
    // instruction of 2026-09-04 makes wrong for half the population: whoever is **not**
    // pre-approved is sent straight into the widget, exactly as they are today, and their
    // submit still mints the token for it.
    if (submitResponse?.isPreApproved !== true) {
      return false;
    }

    // Persona jurídica is not part of this project, and it submits through a different
    // modal besides. Investing the RUC flow in this branch would remove its signature
    // step with nothing to replace it.
    return profile?.role === Roles.INVESTOR && profile?.idType !== IdType.RUC;
  }

  /**
   * D70 · whether the profile that came back from the submit means the popup is due.
   *
   * Only the two facts that arrive with the reloaded document are tested. Role, `idType`
   * and the feature flag are already settled by `shouldDeferSignature` in the enclosing
   * branch, and a second copy of a condition is how the two copies drift apart.
   *
   * ⚠ No `isBlocked` and no "has the investor signed" check. Both states are unreachable
   * from inside the submit that just created the declaration: no admin acts between a
   * request and its own response, and `laftDeclarations.keynuaWidgetSigned` /
   * `keynuaSignedContract` are `false` by construction because the same submit created
   * `keynuaId`. Guarding against them would suggest they can happen.
   */
  isPopupDue(profile: any): boolean {
    return (
      profile?.isPreApproved === true && profile?.userPreApprovedPopUp === false
    );
  }
}
